import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Send, X, Loader2, Globe, Sparkles, BookOpen, 
  GraduationCap, Heart, Brain, Lightbulb, Target, Zap, Bot, 
  School, Briefcase, MapPin, DollarSign, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'हिंदी (Hindi)' },
  { code: 'ta', name: 'தமிழ் (Tamil)' },
  { code: 'te', name: 'తెలుగు (Telugu)' },
  { code: 'bn', name: 'বাংলা (Bengali)' },
  { code: 'mr', name: 'मराठी (Marathi)' },
  { code: 'gu', name: 'ગુજરાતી (Gujarati)' },
  { code: 'kn', name: 'ಕನ್ನಡ (Kannada)' },
  { code: 'ml', name: 'മലയാളം (Malayalam)' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)' },
  { code: 'ar', name: 'العربية (Arabic)' },
  { code: 'es', name: 'Español (Spanish)' },
  { code: 'fr', name: 'Français (French)' },
  { code: 'de', name: 'Deutsch (German)' },
  { code: 'zh', name: '中文 (Chinese)' },
  { code: 'ja', name: '日本語 (Japanese)' },
];

const AI_MODELS = [
  { id: 'claude', name: 'Claude 5.5', description: 'Best for deep reasoning', icon: Brain },
  { id: 'gpt', name: 'GPT-5.2', description: 'Fast & versatile', icon: Zap },
  { id: 'gemini', name: 'Gemini 3', description: 'Quick responses', icon: Sparkles },
];

const QUICK_ACTIONS = [
  { icon: School, label: 'University Advice', prompt: 'Help me choose the best universities for my profile' },
  { icon: Briefcase, label: 'Career Path', prompt: 'Create a 90-day career roadmap for me' },
  { icon: DollarSign, label: 'Scholarships', prompt: 'What scholarships am I eligible for?' },
  { icon: Target, label: 'My Report', prompt: 'Explain my assessment report in detail' },
];

const AryanUltraV3 = ({ isOpen, onClose, token, isInternational = false }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [language, setLanguage] = useState('en');
  const [selectedModel, setSelectedModel] = useState('claude');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [showModelSelect, setShowModelSelect] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !sessionStarted && token) {
      startChatSession();
    }
  }, [isOpen, token, sessionStarted]);

  const startChatSession = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/aryan-ultra/start?language=${language}&model=${selectedModel}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessages([
          {
            role: 'assistant',
            content: data.greeting,
            timestamp: new Date().toISOString(),
            model: data.model,
          },
        ]);
        setSessionStarted(true);
      } else {
        // Fallback greeting
        setMessages([
          {
            role: 'assistant',
            content: isInternational 
              ? "Hey there! 🌍✨ I'm Aryan Ultra - your personal AI mentor powered by Claude, GPT & Gemini! I specialize in helping international students find the perfect universities, scholarships, and career paths. What would you like to explore today?"
              : "Hey there! 🙏✨ I'm Aryan Ultra - your AI-powered career mentor! I have access to Claude Sonnet 5.5, GPT-5.2, and Gemini 3 to give you the BEST advice possible. What's on your mind?",
            timestamp: new Date().toISOString(),
            model: selectedModel,
          },
        ]);
        setSessionStarted(true);
      }
    } catch (error) {
      console.error('Error starting Aryan Ultra session:', error);
      setMessages([
        {
          role: 'assistant',
          content: "Hey! 🙏 I'm Aryan Ultra, your AI-powered mentor. I'm having a tiny connection issue, but send me a message and let's chat!",
          timestamp: new Date().toISOString(),
        },
      ]);
      setSessionStarted(true);
    }
    setIsLoading(false);
  };

  const sendMessage = async (messageText = null) => {
    const textToSend = messageText || inputMessage.trim();
    if (!textToSend || isLoading) return;

    const userMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setShowQuickActions(false);

    try {
      const response = await fetch(`${API_URL}/api/aryan-ultra/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: textToSend,
          language: language,
          model: selectedModel,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.response,
            timestamp: new Date().toISOString(),
            model: data.model,
          },
        ]);
      } else {
        throw new Error('Chat request failed');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "Oops! Had a small hiccup there. 😅 Could you try again? I'm right here!",
          timestamp: new Date().toISOString(),
        },
      ]);
    }
    setIsLoading(false);
  };

  const handleQuickAction = (prompt) => {
    sendMessage(prompt);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (content) => {
    // Convert markdown-like formatting
    return content
      .split('\n')
      .map((line, i) => {
        // Bold
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Bullet points
        if (line.startsWith('- ') || line.startsWith('• ')) {
          return `<li class="ml-4">${line.slice(2)}</li>`;
        }
        // Numbered lists
        if (/^\d+\.\s/.test(line)) {
          return `<li class="ml-4">${line}</li>`;
        }
        return line;
      })
      .join('<br/>');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="aryan-ultra-modal">
      <div className="relative w-full max-w-2xl h-[85vh] bg-gradient-to-b from-[#0D0D0D] to-[#1a1a2e] rounded-3xl border border-white/10 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 flex items-center justify-center">
                <Bot className="h-6 w-6 text-white" />
              </div>
              <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-[#0D0D0D]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                Aryan Ultra
                <span className="text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-300 border border-purple-500/30">
                  AI-Powered
                </span>
              </h2>
              <p className="text-xs text-gray-400">
                {AI_MODELS.find(m => m.id === selectedModel)?.name} • Real-time advice
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Model Selector */}
            <div className="relative">
              <button
                onClick={() => setShowModelSelect(!showModelSelect)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 transition-colors"
              >
                <Brain className="h-4 w-4" />
                <span className="hidden sm:inline">{AI_MODELS.find(m => m.id === selectedModel)?.name}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              
              {showModelSelect && (
                <div className="absolute right-0 mt-2 w-48 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-xl z-10 overflow-hidden">
                  {AI_MODELS.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => {
                        setSelectedModel(model.id);
                        setShowModelSelect(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/10 transition-colors ${
                        selectedModel === model.id ? 'bg-purple-500/20' : ''
                      }`}
                    >
                      <model.icon className={`h-4 w-4 ${selectedModel === model.id ? 'text-purple-400' : 'text-gray-400'}`} />
                      <div>
                        <p className="text-sm text-white">{model.name}</p>
                        <p className="text-xs text-gray-500">{model.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageSelect(!showLanguageSelect)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 transition-colors"
              >
                <Globe className="h-4 w-4" />
                <ChevronDown className="h-3 w-3" />
              </button>
              
              {showLanguageSelect && (
                <div className="absolute right-0 mt-2 w-48 max-h-64 overflow-y-auto bg-[#1a1a2e] border border-white/10 rounded-xl shadow-xl z-10">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setShowLanguageSelect(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                        language === lang.code ? 'bg-blue-500/20 text-blue-300' : 'text-gray-300'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
              data-testid="close-aryan-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'bg-white/5 border border-white/10 text-gray-100'
                }`}
              >
                {message.role === 'assistant' && message.model && (
                  <div className="flex items-center gap-1 mb-2 text-xs text-gray-500">
                    <Brain className="h-3 w-3" />
                    <span>{AI_MODELS.find(m => m.id === message.model)?.name || 'AI'}</span>
                  </div>
                )}
                <div
                  className="text-sm leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                />
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
                  <span className="text-sm text-gray-400">Aryan is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {showQuickActions && messages.length <= 1 && (
          <div className="px-4 pb-2">
            <p className="text-xs text-gray-500 mb-2">Quick Actions:</p>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_ACTIONS.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action.prompt)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 hover:border-purple-500/30 transition-all"
                >
                  <action.icon className="h-4 w-4 text-purple-400" />
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t border-white/10 bg-black/20">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Aryan anything about universities, careers, your report..."
                className="w-full px-4 py-3 pr-12 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500/50 resize-none"
                rows={1}
                disabled={isLoading}
                data-testid="aryan-input"
              />
            </div>
            <Button
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading}
              className="h-12 w-12 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 disabled:opacity-50"
              data-testid="send-message-btn"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
          
          <p className="text-xs text-gray-600 mt-2 text-center">
            Powered by Claude Sonnet 5.5, GPT-5.2 & Gemini 3 • Instant real-time advice
          </p>
        </div>
      </div>
    </div>
  );
};

// Floating Button Component
export const AryanUltraFloatingButton = ({ onClick, isInternational = false }) => {
  const [pulse, setPulse] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setPulse(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <button
      onClick={onClick}
      className={`fixed bottom-6 right-6 z-40 group ${pulse ? 'animate-bounce' : ''}`}
      data-testid="aryan-ultra-float-btn"
    >
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
      
      {/* Button */}
      <div className="relative flex items-center gap-2 px-5 py-4 bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500 rounded-full shadow-2xl hover:scale-105 transition-transform">
        <Bot className="h-6 w-6 text-white" />
        <span className="text-white font-semibold">
          {isInternational ? 'Ask Aryan' : 'Chat with Aryan'}
        </span>
        <Sparkles className="h-4 w-4 text-white/80" />
      </div>

      {/* Badge */}
      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full shadow-lg">
        ULTRA
      </span>
    </button>
  );
};

export default AryanUltraV3;
